// The site accent — the client config's colour, on the CSS tokens.
//
// The accent used to live ONLY in localStorage ('zi_accent_color'): a
// personal pick, in one browser. Nothing ever applied the client's own
// colour, even though the server has been returning it at /api/status
// (client.accent {r,g,b}) all along. So a colleague on a fresh browser, a
// new laptop, an incognito window or localhost saw the stylesheet default
// from web/src/index.css — never the client's colour — and the site looked
// like a different product depending on who opened it.
//
// Three accents, and the precedence is the whole of this module:
//   1. the PERSONAL pick (zi_accent_color) — this person, this browser
//   2. the SITE accent (client config, via /api/status) — everyone else
//   3. the CSS token default — whatever index.css ships
//
// The site accent is CACHED (zi_site_accent_color) because the inline
// script in web/index.html paints the tokens before the bundle exists, and
// a fetch cannot be awaited there: without the cache every cold load flashes
// the stylesheet default until /api/status lands. The cache is a paint
// optimisation, never the source of truth — the fetch overwrites it on
// every boot.
//
// KEEP web/index.html IN SYNC: its inline script re-implements readAccent()
// and accentVars() in ES5 for exactly that pre-paint moment.

export const PERSONAL_KEY = 'zi_accent_color';
export const SITE_KEY = 'zi_site_accent_color';

const HEX_RE = /^#[0-9a-fA-F]{6}$/;

export const isAccentHex = (v) => typeof v === 'string' && HEX_RE.test(v);

const byte = (n) => Math.max(0, Math.min(255, Math.round(Number(n))));
const pair = (n) => byte(n).toString(16).padStart(2, '0');

export function rgbToHex(rgb) {
  if (!rgb || typeof rgb !== 'object') return null;
  const { r, g, b } = rgb;
  if (![r, g, b].every((v) => typeof v === 'number' && Number.isFinite(v))) return null;
  return `#${pair(r)}${pair(g)}${pair(b)}`;
}

export function hexToRgb(hex) {
  if (!isAccentHex(hex)) return null;
  return {
    r: parseInt(hex.slice(1, 3), 16),
    g: parseInt(hex.slice(3, 5), 16),
    b: parseInt(hex.slice(5, 7), 16),
  };
}

// The hover shade. This offset (+23 red, +9 green, +0 blue) is the one
// Settings has always used to derive --accent-hover from a picked colour;
// it is reproduced here rather than re-invented so a site colour and a
// personal pick hover identically. web/index.html carries it a third time.
export function accentVars(hex) {
  const rgb = hexToRgb(hex);
  if (!rgb) return null;
  const hover = { r: byte(rgb.r + 23), g: byte(rgb.g + 9), b: byte(rgb.b + 0) };
  return {
    '--accent': hex,
    '--accent-hover': rgbToHex(hover),
    '--accent-rgb': `${rgb.r}, ${rgb.g}, ${rgb.b}`,
    '--accent-hover-rgb': `${hover.r}, ${hover.g}, ${hover.b}`,
  };
}

// Storage throws outright in some privacy modes, and a colour is never
// worth a blank screen — every read and write here is allowed to fail.
function readKey(key) {
  try {
    const v = globalThis.localStorage?.getItem(key);
    return isAccentHex(v) ? v : null;
  } catch { return null; }
}

function writeKey(key, hex) {
  try { globalThis.localStorage?.setItem(key, hex); return true; } catch { return false; }
}

export const readPersonalAccent = () => readKey(PERSONAL_KEY);
export const readSiteAccent = () => readKey(SITE_KEY);

export function writePersonalAccent(hex) {
  if (!isAccentHex(hex)) return false;
  return writeKey(PERSONAL_KEY, hex);
}

export function clearPersonalAccent() {
  try { globalThis.localStorage?.removeItem(PERSONAL_KEY); return true; } catch { return false; }
}

export function cacheSiteAccent(hex) {
  if (!isAccentHex(hex)) return false;
  return writeKey(SITE_KEY, hex);
}

// What this browser should be painted with right now, personal pick first.
// null means "nothing chosen anywhere" — leave the stylesheet alone.
export const effectiveAccentHex = () => readPersonalAccent() || readSiteAccent();

export function applyAccent(hex) {
  const vars = accentVars(hex);
  if (!vars) return false;
  const style = globalThis.document?.documentElement?.style;
  if (!style) return false;
  for (const [prop, value] of Object.entries(vars)) style.setProperty(prop, value);
  return true;
}

// The boot path: /api/status has answered, so cache the site's colour for
// the next cold paint and apply it — UNLESS this person has picked their
// own, which outranks the site everywhere.
export function applySiteAccent(accent) {
  const hex = typeof accent === 'string' ? accent : rgbToHex(accent);
  if (!isAccentHex(hex)) return null;
  cacheSiteAccent(hex);
  if (readPersonalAccent()) return null;
  applyAccent(hex);
  return hex;
}

export const applySiteAccentFromStatus = (status) => applySiteAccent(status?.client?.accent);

// GET /api/branding/theme — the RESOLVED site colour: the admin's Look and
// Feel choice if the site has one, else the client config's accent. This is
// the boot path; the status shape above is the fallback for a site whose
// data routes are down, where the config accent is all that can be known.
export const applySiteAccentFromTheme = (theme) => applySiteAccent(theme?.accent);
